import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import logo from "../../../Assets/LOGO-GENYDMS(2).png";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// ── Tabs ──────────────────────────
const TAB_EMAIL = "email"; // Send username via email
const TAB_OTP   = "otp";   // Verify via OTP → reveal username on screen

const ForgotUsername = () => {
  const navigate = useNavigate();

  const [tab, setTab] = useState(TAB_OTP); // default to OTP tab

  // ── Shared ──
  const [email, setEmail]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // ── Email tab state ──
  const [emailSent, setEmailSent] = useState(false);

  // ── OTP tab state ──
  const [otpSent, setOtpSent]         = useState(false);
  const [otp, setOtp]                 = useState(["", "", "", "", "", ""]);
  const [verifying, setVerifying]     = useState(false);
  const [revealedUsername, setRevealedUsername] = useState("");
  const [resendCooldown, setResendCooldown]     = useState(0);
  const otpRefs = useRef([]);
  const cooldownTimer = useRef(null);

  const triggerPopup = (msg) => { setError(msg); setShowPopup(true); };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  // ── Start resend cooldown (60s) ────────────
  const startCooldown = () => {
    setResendCooldown(60);
    clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(cooldownTimer.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ════════════════════════════════════════════════════════════════════════════
  // OPTION A — Send username to email
  // ════════════════════════════════════════════════════════════════════════════
  const handleSendEmail = async () => {
    if (!isValidEmail(email)) {
      triggerPopup("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await fetch(`${BACKEND}/api/webmanager/forgot-username/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      // Always show success — prevents enumeration
      setEmailSent(true);
    } catch {
      triggerPopup("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════
  // OPTION B — OTP flow
  // ════════════════════════════════════════════════════════════════════════════
  const handleSendOtp = async (isResend = false) => {
    if (!isValidEmail(email)) {
      triggerPopup("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/webmanager/forgot-username/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (res.ok) {
        setOtpSent(true);
        setOtp(["", "", "", "", "", ""]);
        setRevealedUsername("");
        startCooldown();
      } else if (res.status === 404) {
        triggerPopup("No account found with this email address.");
      } else {
        triggerPopup("Failed to send OTP. Please try again.");
      }
    } catch {
      triggerPopup("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // digits only
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      triggerPopup("Please enter the complete 6-digit OTP.");
      return;
    }

    setVerifying(true);
    try {
      const res = await fetch(`${BACKEND}/api/webmanager/forgot-username/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: email.trim(), otp: otpValue }),
      });

      const data = await res.json();

      if (res.ok) {
        setRevealedUsername(data.username);
      } else if (res.status === 422) {
        triggerPopup("Incorrect or expired OTP. Please try again.");
        setOtp(["", "", "", "", "", ""]);
        otpRefs.current[0]?.focus();
      } else {
        triggerPopup(data.error || "Verification failed. Please try again.");
      }
    } catch {
      triggerPopup("Unable to connect to the server. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  // ── Reset both tabs when switching ────────
  const switchTab = (newTab) => {
    setTab(newTab);
    setEmail("");
    setEmailSent(false);
    setOtpSent(false);
    setOtp(["", "", "", "", "", ""]);
    setRevealedUsername("");
    setResendCooldown(0);
    clearInterval(cooldownTimer.current);
  };

  return (
    <div className="container-fluid p-0 login-container">
      <div className="row g-0 min-vh-100">

        {/* LEFT PANEL */}
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-panel">
          <h1 className="welcome-heading">Forgot Your Username?</h1>
          <div />
          <h1 className="welcome-heading">
            Recover your account with your <span>registered email</span>.
          </h1>
          <div>
            <p>
              Use your registered email address to retrieve your username
              <br /> via email or verify with an OTP.
            </p>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-md-6 d-flex justify-content-center align-items-center right-panel">
          <div className="card shadow border-0" style={{ width: "380px" }}>

            <div
              className="card-header text-center text-white"
              style={{ backgroundColor: "#001F54" }}
            >
              Username Recovery
            </div>

            <div className="card-body px-3 py-3">

              {/* LOGO */}
              <div className="logo-wrapper mx-auto mb-3">
                <img src={logo} alt="Logo" className="logo-img" />
              </div>

              {/* ── TAB SWITCHER ── */}
              <div className="d-flex mb-3 border rounded overflow-hidden" style={{ fontSize: "0.82rem" }}>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill ${tab === TAB_OTP ? "text-white" : "btn-light"}`}
                  style={tab === TAB_OTP ? { backgroundColor: "#001F54" } : {}}
                  onClick={() => switchTab(TAB_OTP)}
                >
                  🔐 Verify with OTP
                </button>
                <button
                  type="button"
                  className={`btn btn-sm flex-fill ${tab === TAB_EMAIL ? "text-white" : "btn-light"}`}
                  style={tab === TAB_EMAIL ? { backgroundColor: "#001F54" } : {}}
                  onClick={() => switchTab(TAB_EMAIL)}
                >
                  📧 Send via Email
                </button>
              </div>

              {/* ════════════════════════════════════════
                  TAB A — Send username via email
              ════════════════════════════════════════ */}
              {tab === TAB_EMAIL && (
                <>
                  {!emailSent ? (
                    <>
                      <p className="text-muted small mb-3">
                        Enter your registered email address. We'll send your username to that address.
                      </p>

                      <label className="form-label fw-bold mb-1">REGISTERED EMAIL:</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        placeholder="Enter your registered email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !loading && handleSendEmail()}
                        autoFocus
                        disabled={loading}
                      />

                      <button
                        type="button"
                        className="btn btn-sm w-100 mt-3 text-white d-flex align-items-center justify-content-center gap-2"
                        style={{ backgroundColor: "#001F54" }}
                        onClick={handleSendEmail}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Sending...
                          </>
                        ) : (
                          "Send Username to Email"
                        )}
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-2">
                      <div style={{ fontSize: 48 }}>📧</div>
                      <h6 className="mt-3 fw-bold" style={{ color: "#001F54" }}>
                        Email Sent!
                      </h6>
                      <p className="text-muted small mb-1">
                        If <strong>{email}</strong> is registered with us, your
                        username has been sent to that address.
                      </p>
                      <p className="text-muted small mb-3">
                        Please check your inbox (and spam folder).
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm w-100 text-white"
                        style={{ backgroundColor: "#001F54" }}
                        onClick={() => navigate("/login")}
                      >
                        Back to Login
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* ════════════════════════════════════════
                  TAB B — OTP verification
              ════════════════════════════════════════ */}
              {tab === TAB_OTP && (
                <>
                  {/* ── Step 1: Enter email → Send OTP ── */}
                  {!otpSent && !revealedUsername && (
                    <>
                      <p className="text-muted small mb-3">
                        Enter your registered email. We'll send a 6-digit OTP to verify your identity and reveal your username.
                      </p>

                      <label className="form-label fw-bold mb-1">REGISTERED EMAIL:</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        placeholder="Enter your registered email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !loading && handleSendOtp()}
                        autoFocus
                        disabled={loading}
                      />

                      <button
                        type="button"
                        className="btn btn-sm w-100 mt-3 text-white d-flex align-items-center justify-content-center gap-2"
                        style={{ backgroundColor: "#001F54" }}
                        onClick={() => handleSendOtp(false)}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Sending OTP...
                          </>
                        ) : (
                          "Send OTP"
                        )}
                      </button>
                    </>
                  )}

                  {/* ── Step 2: Enter OTP ── */}
                  {otpSent && !revealedUsername && (
                    <>
                      <p className="text-muted small mb-1">
                        A 6-digit OTP was sent to <strong>{email}</strong>.
                      </p>
                      <p className="text-muted small mb-3">
                        Enter it below to reveal your username.
                      </p>

                      {/* OTP boxes */}
                      <div className="d-flex justify-content-between gap-1 mb-3">
                        {otp.map((digit, i) => (
                          <input
                            key={i}
                            ref={(el) => (otpRefs.current[i] = el)}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            className="form-control form-control-sm text-center fw-bold"
                            style={{ width: "42px", fontSize: "1.1rem", letterSpacing: 0 }}
                            value={digit}
                            onChange={(e) => handleOtpChange(i, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(i, e)}
                            disabled={verifying}
                            autoFocus={i === 0}
                          />
                        ))}
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm text-white w-100 d-flex align-items-center justify-content-center gap-2"
                        style={{ backgroundColor: "#4CAF50" }}
                        onClick={handleVerifyOtp}
                        disabled={verifying}
                      >
                        {verifying ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Verifying...
                          </>
                        ) : (
                          "Verify OTP"
                        )}
                      </button>

                      {/* Resend OTP */}
                      <div className="text-center mt-2">
                        {resendCooldown > 0 ? (
                          <small className="text-muted">
                            Resend OTP in {resendCooldown}s
                          </small>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            style={{ color: "#001F54", fontSize: "0.82rem" }}
                            onClick={() => handleSendOtp(true)}
                            disabled={loading}
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>

                      <div className="text-center mt-1">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none"
                          style={{ color: "#6c757d", fontSize: "0.78rem" }}
                          onClick={() => { setOtpSent(false); setOtp(["","","","","",""]); }}
                        >
                          ← Change Email
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── Step 3: Reveal username ── */}
                  {revealedUsername && (
                    <div className="text-center py-2">
                      <div style={{ fontSize: 48 }}>🎉</div>
                      <h6 className="mt-3 fw-bold" style={{ color: "#001F54" }}>
                        Your Username
                      </h6>
                      <div
                        className="alert mt-2 mb-3 fw-bold fs-5"
                        style={{
                          backgroundColor: "#e8f4fd",
                          border: "2px solid #001F54",
                          color: "#001F54",
                          letterSpacing: "0.05em",
                        }}
                      >
                        {revealedUsername}
                      </div>
                      <p className="text-muted small mb-3">
                        Please save your username for future logins.
                      </p>
                      <button
                        type="button"
                        className="btn btn-sm w-100 text-white mb-2"
                        style={{ backgroundColor: "#001F54" }}
                        onClick={() =>
                          navigate("/login", { state: { prefilled_username: revealedUsername } })
                        }
                      >
                        Login with this Username
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm w-100 btn-outline-secondary"
                        onClick={() => navigate("/login")}
                      >
                        Back to Login
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Back to Login link (when not in a success/final state) */}
              {!(emailSent || revealedUsername) && (
                <div className="text-center mt-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 text-decoration-none"
                    style={{ color: "#001F54", fontSize: "0.82rem" }}
                    onClick={() => navigate("/login")}
                  >
                    ← Back to Login
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer text-white py-3">
        <div className="ps-5">
          © 2026 <span className="fw-bold">OneFinCore</span> All Rights Reserved.
        </div>
      </footer>

      {/* ERROR POPUP */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h5 className="text-danger text-center">⚠ Error</h5>
            <p className="text-center">{error}</p>
            <button
              className="btn btn-danger w-100"
              type="button"
              onClick={() => { setShowPopup(false); setError(""); }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotUsername;